import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Building2, Loader2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const SupplierTypeReview = ({ onDone }) => {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [move, setMove] = useState(new Set());
  const [busy, setBusy] = useState(false);

  const load = () => axios.get(`${API}/admin/supplier-types`).then((r) => {
    const s = r.data.suspects || [];
    setRows(s);
    setMove(new Set(s.filter((x) => x.suggest_move).map((x) => x.id)));
  }).catch(() => {});
  useEffect(() => { load(); }, []);
  if (!rows.length) return null;

  const toggle = (id) => setMove((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const apply = async () => {
    const keep = rows.filter((r) => !move.has(r.id)).map((r) => r.id);
    if (!window.confirm(`Move ${move.size} to care providers (removed from the directory) and confirm ${keep.length} as real suppliers?`)) return;
    setBusy(true);
    try {
      const r = await axios.post(`${API}/admin/supplier-types`, { move_ids: [...move], keep_ids: keep });
      toast.success(`${r.data.moved} moved to care providers, ${r.data.kept} confirmed as suppliers`);
      setOpen(false);
      await load();
      onDone && onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not reclassify'); }
    finally { setBusy(false); }
  };

  return (
    <div className="border border-amber-200 bg-amber-50 rounded-lg px-4 py-3 mb-4 space-y-3" data-testid="supplier-type-review">
      <div className="flex items-center justify-between gap-3">
        <span className="text-sm text-amber-900 flex items-center gap-2"><Building2 className="w-4 h-4 shrink-0" />{rows.length} directory suppliers look like councils, NHS, housing or care groups.</span>
        <button onClick={() => setOpen((o) => !o)} className="text-xs font-semibold px-3 py-1.5 rounded-lg border border-amber-300 text-amber-900 hover:bg-amber-100 shrink-0" data-testid="supplier-type-review-btn">{open ? 'Hide' : 'Review'}</button>
      </div>
      {open && (
        <>
          <p className="text-xs text-amber-800">Ticked ones move to care providers and leave the directory. Unticked ones are confirmed as real suppliers and won't be flagged again. Careline and telecare services start unticked.</p>
          <div className="max-h-[50vh] overflow-y-auto bg-white rounded-lg border border-amber-100 divide-y divide-gray-100">
            {rows.map((r) => (
              <label key={r.id} className="flex items-center gap-3 px-3 py-2 text-sm cursor-pointer hover:bg-gray-50" data-testid={`supplier-type-row-${r.id}`}>
                <input type="checkbox" checked={move.has(r.id)} onChange={() => toggle(r.id)} className="accent-[#00A79D]" />
                <span className="flex-1 min-w-0 flex flex-col sm:flex-row sm:items-center sm:gap-3">
                  <span className="font-medium text-gray-900 truncate sm:flex-1">{r.name}</span>
                  <span className="text-xs text-gray-400 truncate max-w-[160px] hidden sm:block">{r.domain}</span>
                  <span className={`text-[11px] sm:shrink-0 ${r.suggest_move ? 'text-gray-500' : 'text-[#00857D] font-semibold'}`}>{r.reason}</span>
                </span>
              </label>
            ))}
          </div>
          <div className="flex items-center justify-between gap-3">
            <span className="text-xs text-amber-900">{move.size} to move · {rows.length - move.size} to keep</span>
            <button onClick={apply} disabled={busy} className="inline-flex items-center gap-1.5 text-sm font-semibold px-4 py-2 rounded-lg bg-[#00A79D] text-white hover:bg-[#00857D] disabled:opacity-60" data-testid="supplier-type-apply-btn">
              {busy && <Loader2 className="w-4 h-4 animate-spin" />}Apply
            </button>
          </div>
        </>
      )}
    </div>
  );
};
